import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import NotificationSnackbar from '../../../components/notificationSnackbar';
import { FieldLabel, formFieldSx } from '../../../styles/formStyles';
import { managementCreateButtonSx, managementHeaderContentSx, managementHeaderSx, managementPageSubtitleSx } from '../../../styles/managementStyles';
import { buttonContentSx } from '../../../styles/dialogStyles';
import { createEditCancelButtonSx, createEditPrimaryButtonSx } from '../../../styles/pageFormStyles';
import { getCountries } from '../climate/climate.geo';
import HeavyRainModal from './heavyRainModal';
import { AREA_CAP, VERY_HEAVY_SEVERITY, getAllThresholds, removeThresholds, saveThresholds } from './heavyRain.service';

const cardSx = {
    p: 3,
    borderRadius: '16px',
    border: '1px solid #DFE5F6',
    backgroundColor: '#FFFFFF',
};

const captionSx = {
    fontSize: 12,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: '#625B71',
    fontWeight: 600,
};

function Figure({ caption, value, note }) {
    return (
        <Box>
            <Typography sx={captionSx}>{caption}</Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#1C1B1F', mt: 0.5 }}>{value}</Typography>
            {note && <Typography sx={{ fontSize: 13, color: '#625B71' }}>{note}</Typography>}
        </Box>
    );
}

/**
 * Per-country heavy-rain thresholds for the eK Score Distress Index.
 *
 * A spell of days whose rainfall reaches the country's "very heavy" line becomes one area-wide
 * distress event for the farmers there. The lines are a weather agency's daily rainfall categories, so they differ by
 * country, and a country with none gets no heavy-rain events — which the screen says plainly,
 * because "not configured" must not be mistaken for "no heavy rain".
 */
function HeavyRain() {
    const [countries, setCountries] = useState([]);
    const [countryId, setCountryId] = useState('');
    const [countryNote, setCountryNote] = useState('');
    const [byCountry, setByCountry] = useState({});
    const [loadError, setLoadError] = useState('');
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
    const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

    useEffect(() => {
        let active = true;
        (async () => {
            const { countries: list, failed } = await getCountries();
            if (!active) return;

            setCountries(list);
            setCountryId((current) => (list.some((c) => c.id === current) ? current : list[0]?.id || ''));
            if (failed) setCountryNote('Could not load the country list.');
            else if (list.length === 0) setCountryNote('No active countries are configured.');
            else setCountryNote('');
            setLoading(false);
        })();
        return () => {
            active = false;
        };
    }, []);

    const loadThresholds = useCallback(async () => {
        const result = await getAllThresholds();
        if (!result.success) {
            setLoadError(result.error.message);
            return;
        }
        setLoadError('');
        setByCountry(result.data);
    }, []);

    useEffect(() => {
        loadThresholds();
    }, [loadThresholds]);

    const selectedCountry = countries.find((country) => country.id === countryId) || null;
    const countryName = selectedCountry?.name || 'this country';
    const thresholds = countryId ? byCountry[countryId] || null : null;

    const handleSave = async (draft) => {
        const result = await saveThresholds(countryId, draft);
        if (!result.success) {
            notify(result.error.message, 'error');
            return false;
        }
        setByCountry((current) => ({ ...current, [countryId]: result.data }));
        setModalOpen(false);
        notify(`Heavy-rain thresholds saved for ${countryName}.`);
        return true;
    };

    const handleRemove = async () => {
        const result = await removeThresholds(countryId);
        setConfirmRemove(false);
        if (!result.success) {
            notify(result.error.message, 'error');
            return;
        }
        setByCountry((current) => {
            const next = { ...current };
            delete next[countryId];
            return next;
        });
        notify(`Heavy-rain thresholds removed for ${countryName}.`);
    };

    return (
        <>
            <Box sx={managementHeaderSx}>
                <Box sx={managementHeaderContentSx}>
                    <Typography sx={managementPageSubtitleSx}>
                        The daily rainfall at which heavy rain around a farmer counts as a distress event.
                    </Typography>
                </Box>
            </Box>

            <Alert severity="info" sx={{ borderRadius: '16px', mb: 3 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>This changes how farmers' distress is measured</AlertTitle>
                Each spell of consecutive days in the last year with rainfall (NASA POWER) at or above the very heavy
                line is one distress event, at severity {VERY_HEAVY_SEVERITY}; if any day in it reached the extremely
                heavy line it takes the severity set here. NASA POWER describes an area about 55 km across, so heavy
                rain on its own adds at most {AREA_CAP} to a farmer's distress (Watch), whatever the severity. The lines
                are the country's weather agency's own daily rainfall categories.
                A country without thresholds gets no heavy-rain events: its farmers' environmental distress shows as not
                observed, not as calm.
            </Alert>

            {loadError && (
                <Alert severity="error" sx={{ borderRadius: '16px', mb: 3 }}>{loadError}</Alert>
            )}

            <Paper elevation={0} sx={{ ...cardSx, mb: 3 }}>
                <Box sx={{ maxWidth: 420 }}>
                    <FieldLabel label="Country" />
                    <TextField
                        select
                        fullWidth
                        value={countryId}
                        onChange={(event) => setCountryId(event.target.value)}
                        disabled={loading || countries.length === 0}
                        SelectProps={{ displayEmpty: true }}
                        sx={formFieldSx}
                        data-testid="heavy-rain-country"
                    >
                        <MenuItem value="" disabled>
                            {loading ? 'Loading…' : 'Select a country'}
                        </MenuItem>
                        {countries.map((country) => (
                            <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
                        ))}
                    </TextField>
                    {countryNote && <Typography sx={{ fontSize: 13, color: '#8A5A00', mt: 1 }}>{countryNote}</Typography>}
                </Box>
            </Paper>

            {countryId && !loadError && !thresholds && (
                <Paper elevation={0} sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
                    <Icon icon="tabler:cloud-off" width="40" height="40" color="#94A3B8" />
                    <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1C1B1F', mt: 2, mb: 1 }}>
                        No thresholds for {countryName}
                    </Typography>
                    <Typography sx={{ color: '#625B71', maxWidth: 620, mx: 'auto', mb: 3 }}>
                        Heavy-rain events are not scored for farmers in {countryName}. Set the country's weather agency's
                        daily rainfall categories to start.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => setModalOpen(true)}
                        sx={{ ...managementCreateButtonSx, minWidth: 240 }}
                    >
                        <Box component="span" sx={buttonContentSx}>
                            <Icon icon="tabler:plus" width="18" height="18" />
                            <span>SET THRESHOLDS</span>
                        </Box>
                    </Button>
                </Paper>
            )}

            {countryId && thresholds && (
                <Paper elevation={0} sx={{ ...cardSx, mb: 3 }} data-testid="heavy-rain-thresholds">
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1C1B1F' }}>{countryName}</Typography>
                            <Typography sx={{ fontSize: 14, color: '#625B71' }}>Source: {thresholds.source}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                onClick={() => setModalOpen(true)}
                                startIcon={<Icon icon="tabler:pencil" width="18" height="18" />}
                                sx={{ textTransform: 'none', color: '#294190', fontWeight: 600 }}
                            >
                                Edit
                            </Button>
                            <Button
                                onClick={() => setConfirmRemove(true)}
                                startIcon={<Icon icon="tabler:trash" width="18" height="18" />}
                                sx={{ textTransform: 'none', color: '#DA342E', fontWeight: 600 }}
                            >
                                Remove
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3 }}>
                        <Figure caption="Very heavy from" value={`${thresholds.veryHeavyMm} mm`} note={`Severity ${VERY_HEAVY_SEVERITY}`} />
                        <Figure caption="Extremely heavy from" value={`${thresholds.extremelyHeavyMm} mm`} />
                        <Figure caption="Extremely heavy severity" value={thresholds.extremelyHeavySeverity} note="Out of 10" />
                    </Box>

                    {thresholds.notes && (
                        <Typography sx={{ fontSize: 14, color: '#5B6472', mt: 3 }}>{thresholds.notes}</Typography>
                    )}
                </Paper>
            )}

            <HeavyRainModal
                open={modalOpen}
                countryName={selectedCountry?.name}
                thresholds={thresholds}
                onClose={() => setModalOpen(false)}
                onSave={handleSave}
            />

            <Dialog open={confirmRemove} onClose={() => setConfirmRemove(false)} PaperProps={{ sx: { borderRadius: '24px' } }}>
                <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>Remove thresholds for {countryName}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#5B6472' }}>
                        Heavy-rain events will no longer be scored for farmers in {countryName}. Their environmental
                        distress will show as not observed.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
                    <Button onClick={() => setConfirmRemove(false)} sx={createEditCancelButtonSx}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleRemove}
                        sx={{ ...createEditPrimaryButtonSx, bgcolor: '#DA342E', '&:hover': { bgcolor: '#B42318' } }}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

            <NotificationSnackbar
                open={snackbar.open}
                onClose={closeSnackbar}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </>
    );
}

export default HeavyRain;
